import updateCabinetVisualizer from './updateCabinetVisualizer';
import { loadDesignsByProject } from '../../../api/design/projectSupport';
import { tryGetDesign } from '../../../api/design';
import logNewRelicCustom from '../../../utils/logNewRelicCustom';
import { NEW_RELIC_EVENTS } from '../../../constants';
import { selectCabinetStyleCollectionId } from '../selectors';

/**
 * Load a project and switch to it without page reload
 * Ported from Playground workspace with extensions for HPP-5621
 *
 * @param {Function} set - Zustand set function
 * @param {Function} get - Zustand get function
 * @param {string} projectId - Project ID to load
 * @param {string} host - API host URL (GCC host)
 * @param {Object} options - Optional configuration
 * @param {boolean} options.preserveSelectedDesign - Keep current selectedDesignId if it exists in loaded designs
 * @param {string} options.forceSelectedDesignId - Force a specific design to be selected after load
 * @returns {Promise<{success: boolean, error?: string, selectedDesignId?: string}>} Result object
 */
export default async function loadProject(set, get, projectId, host, options = {}) {
  const { preserveSelectedDesign = false, forceSelectedDesignId = null } = options;

  try {
    const {
      projects,
      selectedDesignId: currentSelectedDesignId
    } = get().cabinetVisualizer;

    // Fetch designs from API
    const apiDesigns = await loadDesignsByProject(host, projectId);

    if (!apiDesigns || !Array.isArray(apiDesigns) || apiDesigns.length === 0) {
      logNewRelicCustom(NEW_RELIC_EVENTS.LOAD_PROJECT_NOT_FOUND, { projectId, reason: 'no designs' });
      return { success: false, error: `No designs found for project ${projectId}` };
    }

    // If project doesn't exist in store, create it from the loaded designs
    let project = projects[projectId];
    if (!project) {
      const firstDesign = apiDesigns[0];
      const created = (firstDesign && firstDesign.Created) ? firstDesign.Created : new Date().toISOString();
      const lastModified = (firstDesign && firstDesign.LastModified) ? firstDesign.LastModified : created;

      project = {
        id: projectId,
        designIds: apiDesigns.map((design) => design.Id),
        collectionId: firstDesign?.CollectionId,
        styleChoiceId: firstDesign?.DesignStyleChoiceid
          ? parseInt(firstDesign.DesignStyleChoiceid, 10)
          : null,
        created,
        lastModified,
      };
    }

    // Determine which design to select
    let selectedDesignId;
    const newDesignIds = apiDesigns.map((design) => design.Id);

    if (forceSelectedDesignId && newDesignIds.includes(forceSelectedDesignId)) {
      // Use explicitly requested design
      selectedDesignId = forceSelectedDesignId;
    } else if (preserveSelectedDesign && currentSelectedDesignId && newDesignIds.includes(currentSelectedDesignId)) {
      // Preserve current selection if it exists in loaded designs
      selectedDesignId = currentSelectedDesignId;
    } else {
      // Default to first design
      selectedDesignId = apiDesigns[0].Id;
    }

    const selectedDesign = apiDesigns.find((design) => design.Id === selectedDesignId) || apiDesigns[0];
    const styleChoiceIdFromDesign = selectedDesign.DesignStyleChoiceid;

    // Resolve collectionId: prefer project's stored value, fall back to API design data,
    // then to style choice metadata (for older designs missing CollectionId)
    const metadataCollectionId = selectCabinetStyleCollectionId({
      ...get().cabinetVisualizer,
      styleChoiceId: parseInt(styleChoiceIdFromDesign, 10),
    });
    const resolvedCollectionId = project.collectionId
      || selectedDesign?.CollectionId
      || metadataCollectionId
      || null;

    // Update URL without page reload (following hydrator pattern)
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);

      if (styleChoiceIdFromDesign) {
        url.searchParams.set('DesignStyleChoiceid', styleChoiceIdFromDesign);
      }
      if (selectedDesignId) {
        url.searchParams.set('designid', selectedDesignId);
      }

      // Update the collection itemId in the URL path
      const pathSegments = url.pathname.split('/').filter(Boolean);
      const lastSegment = pathSegments[pathSegments.length - 1];
      if (resolvedCollectionId) {
        if (/^[0-9]+$/.test(lastSegment)) {
          pathSegments[pathSegments.length - 1] = String(resolvedCollectionId);
        } else {
          pathSegments.push(String(resolvedCollectionId));
        }
      } else if (/^[0-9]+$/.test(lastSegment)) {
        // No collectionId available — remove stale numeric segment from URL
        pathSegments.pop();
      }
      url.pathname = '/' + pathSegments.join('/');

      window.history.replaceState({}, '', url.toString());
    }

    // Filter designs for current style choice to maintain wall tab order
    const filteredStyleDesignIds = newDesignIds.filter((designId) => {
      const design = apiDesigns.find((des) => des.Id === designId);
      return design && parseInt(design.DesignStyleChoiceid, 10) === parseInt(styleChoiceIdFromDesign, 10);
    });

    // Update store state (following loadFromProject pattern)
    const newStyleChoiceId = parseInt(styleChoiceIdFromDesign, 10) || null;
    const previousStyleChoiceId = get().cabinetVisualizer.styleChoiceId;
    const styleChanged = previousStyleChoiceId !== newStyleChoiceId;

    set(
      updateCabinetVisualizer(set, get, {
        currentProjectId: projectId,
        selectedDesignId,
        styleChoiceId: newStyleChoiceId,
        collectionChangeTriggered: styleChanged,
        itemPrices: [],
        designs: apiDesigns.reduce((acc, design) => {
          acc[design.Id] = design;
          return acc;
        }, {}),
        designIds: newDesignIds,
        styleDesignIds: filteredStyleDesignIds, // Maintain wall tab order
        projects: {
          ...projects,
          [projectId]: {
            ...project,
            designIds: newDesignIds,
            collectionId: resolvedCollectionId,
          }
        }
      }),
      false,
      { type: 'cabinetVisualizer/loadProject', projectId }
    );

    return { success: true, selectedDesignId };
  } catch (error) {
    logNewRelicCustom(NEW_RELIC_EVENTS.LOAD_PROJECT_ERROR, {
      projectId,
      errorMessage: error?.message || 'Unknown error'
    });
    return { success: false, error: error.message || 'Unknown error loading project' };
  }
}

/**
 * Reload a single design from the API without affecting other designs
 * Entry point for future template workflows
 *
 * @param {Function} set - Zustand set function
 * @param {Function} get - Zustand get function
 * @param {string} designId - Design ID to reload
 * @param {string} host - API host URL
 * @param {Object} options - Optional configuration
 * @param {boolean} options.makeActive - Make this design the selected design after reload
 * @returns {Promise<{success: boolean, error?: string}>} Result object
 */
export async function reloadDesign(set, get, designId, host, options = {}) {
  const { makeActive = false } = options;

  try {
    const { designs, designIds } = get().cabinetVisualizer;

    // Fetch single design from API
    const updatedDesign = await tryGetDesign(host, designId);

    if (!updatedDesign) {
      logNewRelicCustom(NEW_RELIC_EVENTS.RELOAD_DESIGN_ERROR, { designId, reason: 'not found' });
      return { success: false, error: `Failed to reload design ${designId}` };
    }

    // Update only this design in the store
    const updateData = {
      designs: {
        ...designs,
        [designId]: updatedDesign
      }
    };

    // Optionally make this the active design
    if (makeActive) {
      updateData.selectedDesignId = designId;
    }

    set(
      updateCabinetVisualizer(set, get, updateData),
      false,
      { type: 'cabinetVisualizer/reloadDesign', designId }
    );

    return { success: true };
  } catch (error) {
    logNewRelicCustom(NEW_RELIC_EVENTS.RELOAD_DESIGN_ERROR, {
      designId,
      errorMessage: error?.message || 'Unknown error'
    });
    return { success: false, error: error.message || 'Unknown error reloading design' };
  }
}

/**
 * Add a new design to an existing project
 * Entry point for future template workflows where backend creates a new design
 *
 * @param {Function} set - Zustand set function
 * @param {Function} get - Zustand get function
 * @param {string} designId - New design ID to add
 * @param {string} projectId - Project ID to add the design to
 * @param {string} host - API host URL
 * @param {Object} options - Optional configuration
 * @param {boolean} options.makeActive - Make this design the selected design after adding
 * @returns {Promise<{success: boolean, error?: string}>} Result object
 */
export async function addDesignToProject(set, get, designId, projectId, host, options = {}) {
  const { makeActive = true } = options;

  try {
    const { designs, designIds, styleDesignIds, projects } = get().cabinetVisualizer;

    // Fetch the new design from API
    const newDesign = await tryGetDesign(host, designId);

    if (!newDesign) {
      logNewRelicCustom(NEW_RELIC_EVENTS.ADD_DESIGN_TO_PROJECT_ERROR, { designId, projectId, reason: 'not found' });
      return { success: false, error: `Failed to fetch new design ${designId}` };
    }

    // Ensure design has projectId set
    newDesign.ProjectId = projectId;

    // Add to designs
    const updatedDesigns = {
      ...designs,
      [designId]: newDesign
    };

    // Add to designIds if not already present
    const updatedDesignIds = designIds.includes(designId)
      ? designIds
      : [...designIds, designId];

    // Add to styleDesignIds if it matches current style
    const currentStyleChoiceId = get().cabinetVisualizer.styleChoiceId;
    const designStyleChoiceId = parseInt(newDesign.DesignStyleChoiceid, 10);
    const updatedStyleDesignIds = (designStyleChoiceId === currentStyleChoiceId && !styleDesignIds.includes(designId))
      ? [...styleDesignIds, designId]
      : styleDesignIds;

    // Update project's designIds if needed
    const project = projects[projectId];
    const updatedProjects = project ? {
      ...projects,
      [projectId]: {
        ...project,
        designIds: project.designIds?.includes(designId)
          ? project.designIds
          : [...(project.designIds || []), designId]
      }
    } : projects;

    const updateData = {
      designs: updatedDesigns,
      designIds: updatedDesignIds,
      styleDesignIds: updatedStyleDesignIds,
      projects: updatedProjects
    };

    // Optionally make this the active design
    if (makeActive) {
      updateData.selectedDesignId = designId;

      // Update URL
      if (typeof window !== 'undefined') {
        const url = new URL(window.location.href);
        url.searchParams.set('designid', designId);
        if (newDesign.DesignStyleChoiceid) {
          url.searchParams.set('DesignStyleChoiceid', newDesign.DesignStyleChoiceid);
        }
        window.history.replaceState({}, '', url.toString());
      }
    }

    set(
      updateCabinetVisualizer(set, get, updateData),
      false,
      { type: 'cabinetVisualizer/addDesignToProject', designId, projectId }
    );

    return { success: true };
  } catch (error) {
    logNewRelicCustom(NEW_RELIC_EVENTS.ADD_DESIGN_TO_PROJECT_ERROR, {
      designId,
      projectId,
      errorMessage: error?.message || 'Unknown error'
    });
    return { success: false, error: error.message || 'Unknown error adding design to project' };
  }
}
